/**
 * VAT Type Mappings (Frontend) - KROK 9.2
 *
 * Central mapping configuration between VAT types and their numeric rates.
 * Synchronized with backend VatRateHelper.cs
 *
 * @module vatMappings
 * @version 2.0.0 (Simplified without RefundSource)
 * @synchronized_with TravelCRM.Application/Invoices/Helpers/VatRateHelper.cs
 */

import { VatRateType, InvoiceType, InvoiceScheme } from '../Types/invoicesTypes';

/**
 * Maps VAT type enum to numeric percentage rate
 *
 * @synchronized_with VatRateHelper.cs::GetRateFromType()
 */
export const VAT_TYPE_TO_RATE_MAP: Record<VatRateType, number> = {
  [VatRateType.Vat23]: 23,
  [VatRateType.Vat8]: 8,
  [VatRateType.Vat5]: 5,
  [VatRateType.VatZW]: 0, // Changed from Vat0
  [VatRateType.VatNP]: 0, // Changed from VatNp
  [VatRateType.VatExempt]: 0,
  [VatRateType.VatEU]: 0, // NEW: Intra-EU supply
  [VatRateType.VatMargin]: 0,
  [VatRateType.VatRehab]: 0, // NEW: Rehabilitation services
  [VatRateType.Vat0Domestic]: 0, // NEW: 0% domestic (KSeF "0 KR")
};

/**
 * Determines if a VAT type is exempt (requires legal note)
 *
 * @synchronized_with VatRateHelper.cs::IsExempt()
 */
export const VAT_TYPE_IS_EXEMPT_MAP: Record<VatRateType, boolean> = {
  [VatRateType.Vat23]: false,
  [VatRateType.Vat8]: false,
  [VatRateType.Vat5]: false,
  [VatRateType.VatZW]: true,
  [VatRateType.VatNP]: true,
  [VatRateType.VatExempt]: true,
  [VatRateType.VatEU]: true,
  [VatRateType.VatMargin]: true,
  [VatRateType.VatRehab]: true,
  [VatRateType.Vat0Domestic]: false, // numeric 0% — nie wymaga podstawy prawnej
};

/**
 * Display labels for VAT types (Polish)
 *
 * @synchronized_with VatRateTypeLabels.cs
 */
export const VAT_TYPE_DISPLAY_LABELS: Record<VatRateType, string> = {
  [VatRateType.Vat23]: '23% VAT',
  [VatRateType.Vat8]: '8% VAT',
  [VatRateType.Vat5]: '5% VAT',
  [VatRateType.VatZW]: 'ZW - Zwolnione z VAT',
  [VatRateType.VatNP]: 'NP - Nie podlega VAT',
  [VatRateType.VatExempt]: 'Zwolnione z VAT (ogólne)',
  [VatRateType.VatEU]: 'WDT - Wewnątrzunijna dostawa towarów',
  [VatRateType.VatMargin]: 'Procedura marży',
  [VatRateType.VatRehab]: 'Turnusy rehabilitacyjne (zwolnione)',
  [VatRateType.Vat0Domestic]: '0% VAT (krajowe)',
};

/**
 * Short labels for PDF columns
 *
 * @synchronized_with VatRateHelper.cs::GetShortLabel()
 */
export const VAT_TYPE_SHORT_LABELS: Record<VatRateType, string> = {
  [VatRateType.Vat23]: '23%',
  [VatRateType.Vat8]: '8%',
  [VatRateType.Vat5]: '5%',
  [VatRateType.VatZW]: 'ZW',
  [VatRateType.VatNP]: 'NP',
  [VatRateType.VatExempt]: 'ZW',
  [VatRateType.VatEU]: 'WDT',
  [VatRateType.VatMargin]: 'Marża',
  [VatRateType.VatRehab]: 'Rehab',
  [VatRateType.Vat0Domestic]: '0%',
};

/**
 * Default legal notes for exempt VAT types
 *
 * @synchronized_with VatRateHelper.cs::GetDefaultLegalBasis()
 * @note Simplified version without RefundSource parameter
 */
export const VAT_TYPE_DEFAULT_NOTES: Record<VatRateType, string> = {
  [VatRateType.Vat23]: '',
  [VatRateType.Vat8]: '',
  [VatRateType.Vat5]: '',
  [VatRateType.VatZW]: 'Zwolnienie z VAT zgodnie z Art. 43 ust. 1 ustawy o VAT.',
  [VatRateType.VatNP]: '', // Filled dynamically based on InvoiceType
  [VatRateType.VatExempt]: 'Zwolnienie z VAT zgodnie z Art. 43 ust. 1 ustawy o VAT.',
  [VatRateType.VatEU]:
    'Wewnątrzunijna dostawa towarów (WDT) zgodnie z Art. 42 ust. 1 ustawy o VAT.',
  [VatRateType.VatMargin]:
    'Procedura marży dla biur podróży (Art. 119 ustawy o VAT) – VAT nie jest wykazywany.',
  [VatRateType.VatRehab]:
    'Usługi rehabilitacyjne – zwolnione z VAT zgodnie z Art. 43 ust. 1 pkt 18 ustawy o VAT.',
  [VatRateType.Vat0Domestic]: '',
};

/**
 * Gets numeric VAT rate from VAT type
 */
export const getVatRate = (vatType: VatRateType): number => {
  // Normalize to PascalCase (backend may send camelCase like "vatRehab")
  const normalizedType = normalizeVatType(vatType);
  const rate = VAT_TYPE_TO_RATE_MAP[normalizedType];
  if (rate === undefined) {
    throw new Error(`Invalid VAT type: ${vatType} (normalized: ${normalizedType})`);
  }
  return rate;
};

/**
 * Normalizes VAT type to PascalCase format.
 * Backend sends: "vatRehab", frontend expects: "VatRehab"
 */
/**
 * Normalizes VAT type string to enum value
 * Handles case differences: vat23 → Vat23, vatZW → VatZW
 */
export const normalizeVatType = (vatType: VatRateType | string): VatRateType => {
  // If already valid enum, return as-is
  if (Object.values(VatRateType).includes(vatType as VatRateType)) {
    return vatType as VatRateType;
  }

  // Try capitalizing first letter (vatRehab → VatRehab, vat23 → Vat23)
  const str = String(vatType);
  const normalized = str.charAt(0).toUpperCase() + str.slice(1);

  if (Object.values(VatRateType).includes(normalized as VatRateType)) {
    return normalized as VatRateType;
  }

  // Fallback: return original
  return vatType as VatRateType;
};

/**
 * Checks if VAT type requires exemption note
 */
export const isVatExempt = (vatType: VatRateType): boolean => {
  const normalizedType = normalizeVatType(vatType);
  return VAT_TYPE_IS_EXEMPT_MAP[normalizedType] ?? false;
};

/**
 * Checks if VAT type is margin scheme
 */
export const isMarginScheme = (vatType: VatRateType): boolean => {
  const normalizedType = normalizeVatType(vatType);
  return normalizedType === VatRateType.VatMargin;
};

/**
 * Validates if a value is a valid VatRateType
 */
export const isValidVatType = (value: unknown): value is VatRateType => {
  return typeof value === 'string' && Object.values(VatRateType).includes(value as VatRateType);
};

/**
 * Gets default VAT exempt note based on VAT type and invoice type
 *
 * @param vatType - VAT type enum value
 * @param invoiceType - Invoice type (for PEFRO detection)
 * @returns Default legal note or empty string
 */
export const getDefaultVatExemptNote = (
  vatType: VatRateType,
  invoiceType?: InvoiceType
): string => {
  const normalizedType = normalizeVatType(vatType);

  // Special handling for VatNP based on invoice type (PEFRO refunds)
  // Case-insensitive comparison because backend may return 'refund' (lowercase)
  if (normalizedType === VatRateType.VatNP && String(invoiceType).toLowerCase() === 'refund') {
    return 'Refundacja z PFRON – nie podlega VAT zgodnie z Art. 113 ust. 1 ustawy o VAT.';
  }

  return VAT_TYPE_DEFAULT_NOTES[normalizedType] || '';
};

/**
 * Gets display label for VAT type
 */
export const getDisplayLabel = (vatType: VatRateType): string => {
  const normalizedType = normalizeVatType(vatType);
  return VAT_TYPE_DISPLAY_LABELS[normalizedType] || vatType;
};

/**
 * Gets short label for VAT type (for PDF columns)
 */
export const getShortLabel = (vatType: VatRateType): string => {
  const normalizedType = normalizeVatType(vatType);
  return VAT_TYPE_SHORT_LABELS[normalizedType] || vatType;
};

/**
 * Calculates gross amount from net amount and VAT type
 *
 * @synchronized_with VatRateHelper.cs::CalculateGrossAmount()
 */
export const calculateGrossFromNet = (netAmount: number, vatType: VatRateType): number => {
  if (!Number.isFinite(netAmount)) return 0;

  // Margin scheme: gross = net (no VAT added)
  if (isMarginScheme(vatType)) {
    return netAmount;
  }

  const vatRate = getVatRate(vatType);
  const vatAmount = Math.round(netAmount * (vatRate / 100) * 100) / 100;
  return netAmount + vatAmount;
};

/**
 * Calculates VAT amount from net amount and VAT type
 *
 * @synchronized_with VatRateHelper.cs::CalculateVatAmount()
 */
export const calculateVatAmount = (netAmount: number, vatType: VatRateType): number => {
  if (!Number.isFinite(netAmount)) return 0;

  // Margin scheme or exempt: no VAT
  if (isMarginScheme(vatType) || isVatExempt(vatType)) {
    return 0;
  }

  const vatRate = getVatRate(vatType);
  return Math.round(netAmount * (vatRate / 100) * 100) / 100;
};

/**
 * Gets allowed VAT types for given invoice scheme and type
 *
 * @param scheme - Invoice scheme (Standard or TravelMargin)
 * @param type - Invoice type
 * @returns Array of allowed VAT types
 *
 * @remarks
 * - TravelMargin scheme: only VatMargin allowed
 * - Refund type: ONLY VatNP allowed (backend validation: PEFRO refunds must use VatNP per Art. 113 ust. 1)
 * - Standard scheme: all types except VatMargin
 *
 * @synchronized_with InvoiceSchemeVatRules.cs::GetAllowedVatTypes()
 */
export const getAllowedVatTypesForInvoice = (
  scheme: InvoiceScheme,
  type: InvoiceType
): VatRateType[] => {
  // Travel margin: only VatMargin
  if (scheme === InvoiceScheme.TravelMargin) {
    return [VatRateType.VatMargin];
  }

  // Refund type: ONLY VatNP allowed (backend validation requirement)
  // PEFRO refunds must have VatType = VatNP (Art. 113 ust. 1 - not subject to VAT)
  // Case-insensitive comparison because backend may return 'refund' (lowercase)
  if (String(type).toLowerCase() === 'refund') {
    return [VatRateType.VatNP];
  }

  // Standard scheme: all except VatMargin
  return [
    VatRateType.Vat23,
    VatRateType.Vat8,
    VatRateType.Vat5,
    VatRateType.VatZW,
    VatRateType.VatNP,
    VatRateType.VatExempt,
    VatRateType.VatEU,
    VatRateType.VatRehab,
    VatRateType.Vat0Domestic,
  ];
};

/**
 * Gets preferred default VAT type for current scheme/type context.
 * This is used when an existing item has a VAT type outside the allowed set
 * (for example after changing scheme from Standard to TravelMargin).
 */
export const getPreferredVatTypeForInvoice = (
  scheme: InvoiceScheme,
  type: InvoiceType
): VatRateType => {
  if (scheme === InvoiceScheme.TravelMargin) {
    return VatRateType.VatMargin;
  }

  if (type === InvoiceType.Refund) {
    return VatRateType.VatNP;
  }

  const allowed = getAllowedVatTypesForInvoice(scheme, type);

  if (allowed.includes(VatRateType.Vat23)) {
    return VatRateType.Vat23;
  }

  return allowed[0] ?? VatRateType.Vat23;
};

// Deprecated function kept for backwards compatibility
export const isVatDisplayedPerItem = (vatType: VatRateType): boolean => {
  return !isMarginScheme(vatType);
};
